import { useCallback, useEffect, useRef, useState } from "react";

export const BN254_PRIME = BigInt(
  "21888242871839275222246405745257275088548364400416034343698204186575808495617",
);

/** Renewal is offered this long before a handle record lapses. */
export const HANDLE_RENEW_WITHIN_SECONDS = 14 * 24 * 60 * 60;

/** What the handle panel shows about the account's handle. */
export interface ClaimedHandleDetails {
  handle: string;
  networks: string[];
  expiresAt: number;
}

export function useHandleRegistrationFormState<TSpendingKeys>(
  spendingKeys: TSpendingKeys | null,
) {
  const [handleInput, setHandleInput] = useState("");
  const [handleStatus, setHandleStatus] = useState("");
  const [claimedHandle, setClaimedHandle] = useState<string | null>(null);
  const currentOwnerKeysRef = useRef<TSpendingKeys | null>(spendingKeys);

  useEffect(() => {
    if (currentOwnerKeysRef.current !== spendingKeys || !spendingKeys) {
      setClaimedHandle(null);
      setHandleInput("");
      setHandleStatus("");
    }
    currentOwnerKeysRef.current = spendingKeys;
  }, [spendingKeys]);

  return {
    handleInput,
    setHandleInput,
    handleStatus,
    setHandleStatus,
    claimedHandle,
    setClaimedHandle,
    currentOwnerKeysRef,
  };
}

export function useHandleCryptoHelpers() {
  const poseidonRef = useRef<any>(null);

  const ensurePoseidon = useCallback(async () => {
    if (poseidonRef.current) {
      return poseidonRef.current;
    }
    const circomlib = (await import("circomlibjs")) as unknown as {
      buildPoseidon: () => Promise<any>;
    };
    const poseidon = await circomlib.buildPoseidon();
    poseidonRef.current = poseidon;
    return poseidon;
  }, []);

  const toFieldHex = useCallback((value: bigint) => {
    const reduced = ((value % BN254_PRIME) + BN254_PRIME) % BN254_PRIME;
    return `0x${reduced.toString(16).padStart(64, "0")}`;
  }, []);

  const bytes32ToBigInt = useCallback((hex: string) => BigInt(hex), []);

  return { ensurePoseidon, toFieldHex, bytes32ToBigInt };
}
